import { ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { ValidationService } from './ValidationService';
import { ForecastGridRowData, ForecastTemplateColumns } from 'src/models/ForecastModels';
import ForecastValidationMessages from '../forecast-utils/ValidationMessages';
import { convertMonthFormatToDisplay } from 'src/utils/date-time-utilities';
import { MAX_FRACTIONAL_DIGITS_ALLOWED, roundToPrecision } from 'src/utils/ag-grid-utils';
import { ForecastGridFixedFields } from '../forecast-utils/ForecastGridConstants';
import { logger } from 'src/analytics/KatalLogger';
import { BusinessGroupEntity } from 'src/models/AppContextModels';

/**
 * Service for validating forecast month data
 */
export class ForecastMonthValueValidationService extends ValidationService {
  /**
   * Transforms forecast month values in the provided row data by parsing strings to floats and handling null values.
   * Applicable only to file upload.
   *
   * @param forecastTemplateRelevantRowData The relevant row data from the forecast template.
   * @param forecastTemplateColumns The forecast template columns configuration.
   * @returns Promise with transformed data and validation status
   */
  public async parseAndValidateForecastMonthValues(
    businessGroup: BusinessGroupEntity,
    forecastTemplateRelevantRowData: ForecastGridRowData[],
    forecastTemplateColumns: ForecastTemplateColumns
  ): Promise<{ transformedData: ForecastGridRowData[]; validationStatus: ValidationStatusEntity }> {
    const corpSegmentMandatoryFields = forecastTemplateColumns.corpSegmentMandatoryFields;
    const businessSegmentFields = forecastTemplateColumns.businessSegmentColumns;

    const forecastMonthColumnsIds = forecastTemplateColumns.forecastMonthColumnsIds;
    const validationErrorDetails: ValidationErrorDetail[] = [];

    const transformedData = forecastTemplateRelevantRowData.map((row, rowIndex) => {
      const updatedRow = { ...row };
      const rowNo = (row[ForecastGridFixedFields.FileRowNumber.value] as number) ?? rowIndex + 1;

      // Convert corp segment values to strings
      corpSegmentMandatoryFields.forEach((column) => {
        updatedRow[column] = row[column] == null ? null : String(row[column]);
      });

      businessSegmentFields.forEach((businessSegmentField) => {
        const segmentDataType =
          businessGroup.business_segments.find((segment) => segment.business_segment_name === businessSegmentField)?.business_segment_data_type ||
          'text';

        if (segmentDataType === 'date_string') {
          updatedRow[businessSegmentField] = row[businessSegmentField] == null ? null : String(row[businessSegmentField]);
        }

        // This particular logic only cares about converting it to number.
        // Remaining validations happens in corresponding place.
        if (segmentDataType === 'number' && row[businessSegmentField]) {
          updatedRow[businessSegmentField] = isNaN(row[businessSegmentField]) ? row[businessSegmentField] : parseFloat(row[businessSegmentField]);
        }
      });

      // Process forecast month values
      forecastMonthColumnsIds.forEach((column) => {
        const value = updatedRow[column];

        // Handle null/empty values
        if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
          updatedRow[column] = null;
        } else {
          // Parse and validate numeric values
          const parsedValue = parseFloat(value);
          if (isNaN(parsedValue)) {
            validationErrorDetails.push({
              rowIndex: rowNo,
              message: `Invalid number at column '${convertMonthFormatToDisplay(column)}' - ${updatedRow[column]} ${this.getLineItemIdMessagePart(
                row,
                ForecastGridFixedFields.XptLineItemId.value
              )}.`
            });
            updatedRow[column] = null;
          } else {
            // Round to specified precision
            updatedRow[column] = roundToPrecision(parsedValue, MAX_FRACTIONAL_DIGITS_ALLOWED);
          }
        }
      });

      return updatedRow;
    });

    // Log validation errors if any
    this.logValidationErrors('Forecast Month', validationErrorDetails);

    // Return appropriate validation status and transformed data
    const validationStatus: ValidationStatusEntity =
      validationErrorDetails.length > 0
        ? this.createErrorStatus(
            ForecastValidationMessages.FORECAST_MONTH_DATA_VALIDATION_FAILED,
            ForecastValidationMessages.FORECAST_MONTH_DATA_VALIDATION_DEFAULT,
            validationErrorDetails
          )
        : this.createSuccessStatus(
            ForecastValidationMessages.FORECAST_MONTH_DATA_VALIDATION_SUCCESS,
            ForecastValidationMessages.FORECAST_MONTH_DATA_VALIDATION_DEFAULT
          );

    return { transformedData, validationStatus };
  }
}

// Export singleton instance
export const forecastMonthValidationService = new ForecastMonthValueValidationService();
